// React subscription hook for the activity-dock assist-level user
// preference. Slice B2.2 of
// docs/planning/activity_dock_workflow_facilitation.md.
//
// The hook mirrors `useEffect`-based store-subscription patterns used
// elsewhere in the codebase (see `subscribeVoiceSettings` /
// `SettingsPopover`). Initial value comes from `getAssistLevel()` so
// SSR renders the storage default; subsequent writes propagate through
// `subscribeAssistLevel`.

import { useEffect, useState, useCallback } from "react";
import {
  getAssistLevel,
  setAssistLevel as persistAssistLevel,
  subscribeAssistLevel,
  type ActivityDockAssistLevel,
} from "@/lib/activityDock/assistLevel";

export type UseActivityDockAssistLevelResult = {
  level: ActivityDockAssistLevel;
  setLevel: (next: ActivityDockAssistLevel) => void;
};

export function useActivityDockAssistLevel(): UseActivityDockAssistLevelResult {
  const [level, setLevelState] = useState<ActivityDockAssistLevel>(
    getAssistLevel,
  );

  useEffect(() => {
    const unsubscribe = subscribeAssistLevel(setLevelState);
    // Re-sync on mount in case the storage value changed before the
    // subscription attached (e.g. between SSR and hydration).
    setLevelState(getAssistLevel());
    return unsubscribe;
  }, []);

  const setLevel = useCallback((next: ActivityDockAssistLevel) => {
    persistAssistLevel(next);
    // No need to setLevelState locally — subscribeAssistLevel will fire
    // synchronously below the persistAssistLevel call.
  }, []);

  return { level, setLevel };
}
